/**
 * Reading the touch log back -- the query and the labels behind TouchHistory.
 *
 * Named for the concern rather than the component on purpose: a `touchHistory.ts`
 * beside `TouchHistory.tsx` differs only in case, which resolves to one file on
 * macOS and two on Linux. Same reason `touchActions.ts` is not `TouchButtons.ts`.
 *
 * WHY THIS EXISTS AT ALL
 * ----------------------
 * `engagements` was write-only. Two buttons and three database triggers put
 * rows in, `contacts.last_contacted_at` was derived from them, and the whole
 * accountability layer -- contacts_overdue, contacts_stalled, the Monday
 * digest -- ran off that number. But nothing in the app ever showed the
 * evidence.
 *
 * That is a worse failure than it looks. A derived clock you cannot audit is
 * indistinguishable from a clock that is wrong: when the digest says someone
 * is stale and you believe you called them last week, there is no screen that
 * settles it. You stop trusting the digest, and the Zoho failure comes back
 * wearing better fonts. Showing the log is what makes the number checkable.
 *
 * WHY THE QUERY AND THE LABELS ARE HERE AND NOT IN THE COMPONENT
 * ---------------------------------------------------------------
 * The `marketing` vitest project globs plain `.ts` and runs in Node, while
 * `app` runs in real Chromium and costs a ~130 MB Playwright download. The two
 * rules below are the ones that can silently corrupt what a person reads, and
 * neither needs a DOM to check -- so they should not sit one browser install
 * away from being skipped.
 */

import type { Identifier } from "ra-core";
import type { EngagementKind, EngagementSource } from "./types";

/**
 * How many touches the CONTACT PAGE shows.
 *
 * Three, because this panel sits above the notes feed and every row it adds
 * pushes the rest of the page down. A contact with forty touches would have
 * turned their own page into a scrolling log -- the full history has its own
 * page for that.
 */
export const TOUCH_HISTORY_PAGE_SIZE = 3;

/** Ceiling on the full-history page. More than this is not a real case. */
export const TOUCH_HISTORY_EXPANDED_SIZE = 200;

/**
 * The query behind the list.
 *
 * SORTS ON occurred_at, NEVER created_at, AND THAT IS THE WHOLE POINT.
 *
 * The two are different columns for a reason that runs through this entire
 * feature: `occurred_at` is when contact HAPPENED and is deliberately
 * back-dateable, `created_at` is when the row was typed. Logging Tuesday's
 * call on Thursday writes occurred_at = Tuesday and created_at = Thursday.
 *
 * Sorting by created_at looks identical in every ordinary case and reorders
 * exactly the back-dated ones -- so the history would tell you the Tuesday
 * call was the most recent thing that happened, and the cadence clock, which
 * IS max(occurred_at), would disagree with the list that is supposed to
 * explain it. Two views of one fact that disagree is the failure this schema
 * was shaped to avoid.
 */
export function engagementListParams(
  contactId: Identifier,
  perPage: number = TOUCH_HISTORY_PAGE_SIZE,
) {
  return {
    filter: { contact_id: contactId },
    sort: { field: "occurred_at", order: "DESC" as const },
    pagination: { page: 1, perPage },
  };
}

/**
 * Every kind the database will accept, mirrored from the CHECK constraint in
 * 048_lifecycle_core.sql. Exported so a test can assert the labels below cover
 * all of them: adding a kind in SQL and forgetting the label here would render
 * a blank row, and a blank row in an audit log is worse than an ugly one.
 */
export const ENGAGEMENT_KINDS: EngagementKind[] = [
  "call",
  "email",
  "met",
  "other",
  "note",
  "task",
  "delivered",
  "opened",
  "clicked",
];

export const ENGAGEMENT_SOURCES: EngagementSource[] = [
  "manual",
  "note",
  "task",
  "resend",
  "import",
];

const KIND_LABELS: Record<EngagementKind, string> = {
  call: "Call",
  email: "Email",
  met: "Met",
  other: "Contact",
  note: "Note",
  task: "Task completed",
  delivered: "Email delivered",
  opened: "Email opened",
  clicked: "Link clicked",
};

/**
 * How the row came to exist. Shown on every row, because the central claim of
 * this feature is that touches are INFERRED from work you already do -- you
 * write a note, you tick off a task, and a touch appears without you asking.
 * An inference you cannot see is an inference you cannot trust, and the first
 * time an unexplained row shows up people assume the log is making things up.
 */
const SOURCE_ORIGINS: Record<EngagementSource, string> = {
  manual: "logged by hand",
  note: "from a note",
  task: "from a completed task",
  resend: "from Resend",
  import: "imported",
};

/**
 * Kinds that count as US contacting THEM.
 *
 * MUST MIRROR THE ALLOWLIST IN private.refresh_last_contacted_at()
 * (db/migrations/060_outreach_only_clock.sql). That function is the only
 * writer of `contacts.last_contacted_at`, and this list decides what the
 * contact page prints next to "Last contacted". If the two drift, the
 * headline on the page and the number driving the digest disagree about the
 * same person -- which is the one failure the whole panel exists to prevent.
 *
 * `delivered` is ours: our message arriving at their server. `opened` and
 * `clicked` are theirs, and are absent rather than excluded -- an ALLOWLIST,
 * for the same reason the SQL uses one. A kind nobody has classified then
 * fails toward "does not reset the clock", so the contact keeps appearing in
 * the digest and you chase someone twice. The denylist fails the other way:
 * the clock moves, the person drops off the list, and nothing says so.
 * Between "annoying and visible" and "loses someone silently", take the first.
 */
const OUTREACH_KINDS: ReadonlySet<string> = new Set([
  "call",
  "email",
  "met",
  "other",
  "note",
  "task",
  "delivered",
]);

/** True when this row is something we did, and so resets the follow-up clock. */
export function countsAsOutreach(kind: string): boolean {
  return OUTREACH_KINDS.has(kind);
}

/**
 * The most recent thing WE did, from a list already sorted occurred_at DESC.
 *
 * This is what the panel prints as "Last contacted", and it is deliberately
 * computed the same way the database computes the column: max(occurred_at)
 * over outreach kinds only. Taking the first row of the list regardless of
 * kind would print the date of a click -- so a contact who opened an email
 * yesterday would read "Last contacted yesterday" while the digest, correctly,
 * still called them overdue from three weeks ago.
 */
export function lastOutreachAt(
  engagements: readonly { kind: string; occurred_at: string }[],
): string | null {
  return engagements.find((e) => countsAsOutreach(e.kind))?.occurred_at ?? null;
}

export type EngagementDescription = {
  /** Human label for the kind. Never empty. */
  label: string;
  /** Where the row came from, as a phrase. Never empty. */
  origin: string;
  /** True when this counts as us contacting them, and so moves the clock. */
  countsAsOutreach: boolean;
};

/**
 * Turn a stored row into the three strings the list renders.
 *
 * Unknown values fall through to the raw string rather than to a blank. The
 * database CHECK constraint can gain a value in a migration that ships before
 * the frontend knows about it, and in that window an unfamiliar label is
 * informative while an empty cell is a bug report.
 */
export function describeEngagement(engagement: {
  kind: string;
  source: string;
}): EngagementDescription {
  const { kind, source } = engagement;
  return {
    label: KIND_LABELS[kind as EngagementKind] ?? kind,
    origin: SOURCE_ORIGINS[source as EngagementSource] ?? `from ${source}`,
    countsAsOutreach: countsAsOutreach(kind),
  };
}

/** Characters of note text a row shows before it stops. */
export const TOUCH_NOTE_PREVIEW = 90;

/**
 * A one-line preview of a note, and whether anything was left out.
 *
 * TWO PROBLEMS, AND THE WHITESPACE ONE IS THE WORSE OF THEM.
 *
 * Notes carry real newlines -- the trigger copies the first 500 characters of
 * a contact note verbatim -- so rendering one raw either breaks the row onto
 * many lines or, with CSS truncation, hides everything after the first line
 * with no sign that more exists. Collapsing whitespace first makes the preview
 * one honest line whatever the source looked like.
 *
 * Then the length. Cut at the last space before the limit rather than
 * mid-word: a preview ending "…" reads as deliberate, one ending "confir…"
 * reads as damage.
 *
 * `truncated` is returned rather than inferred from the text, because a note
 * that genuinely ends in an ellipsis would otherwise be mistaken for a cut
 * one and offer to open a modal showing exactly what is already on screen.
 */
export function notePreview(
  note: string | null | undefined,
  max: number = TOUCH_NOTE_PREVIEW,
): { text: string; truncated: boolean } {
  const flat = (note ?? "").replace(/\s+/g, " ").trim();
  if (flat.length <= max) return { text: flat, truncated: false };

  const cut = flat.slice(0, max);
  const lastSpace = cut.lastIndexOf(" ");
  // Only honor a word boundary that is not absurdly early, or a single long
  // token would collapse the preview to almost nothing.
  const head = lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut;
  return { text: `${head}…`, truncated: true };
}
